import React, { createContext, PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react'

interface PseudolocContextValue {
  enabled: boolean
  toggle: () => void
}

const PseudolocContext = createContext<PseudolocContextValue | undefined>(undefined)

const STORAGE_KEY = 'i18n:pseudoloc:enabled'

export function PseudolocProvider({ children }: PropsWithChildren) {
  const [enabled, setEnabled] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === 'true'
    } catch {
      return false
    }
  })

  const toggle = useCallback(() => {
    setEnabled((prev) => {
      const newValue = !prev
      try {
        sessionStorage.setItem(STORAGE_KEY, String(newValue))
      } catch {}
      return newValue
    })
  }, [])

  // Keyboard shortcut: Cmd+Alt+Shift+P (Mac) or Ctrl+Alt+Shift+P (Windows/Linux)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for cmd+alt+shift+p (Mac) or ctrl+alt+shift+p (Windows/Linux)
      if (
        (event.metaKey || event.ctrlKey) &&
        event.altKey &&
        event.shiftKey &&
        event.code === 'KeyP'
      ) {
        event.preventDefault()
        toggle()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [toggle])

  return (
    <PseudolocContext.Provider value={{ enabled, toggle }}>
      {children}
    </PseudolocContext.Provider>
  )
}

export function usePseudoloc(): PseudolocContextValue {
  const context = useContext(PseudolocContext)
  // Return default disabled state if context is not available (e.g., SSR)
  if (context === undefined) {
    return { enabled: false, toggle: () => {} }
  }
  return context
}
